import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin, Navigation, Upload, CreditCard, FileDown, LifeBuoy, Plus,
  CheckCircle2, Clock, Copy, FileText, Phone, ReceiptEuro, AlertCircle, PhoneCall, Headset, FileSignature,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { pdfUrl } from "@/lib/files";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { money, formatDateTime } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const ACTIVE = ["pending", "confirmed", "in_transit"];
const OPEN_QUOTE = ["new", "sent", "pending", "draft"];

const STEP_LABELS = {
  it: ["Preventivo", "Pagamento", "Documenti", "Partner assegnato", "Partner accettato", "Autista assegnato", "In transito", "Consegnato"],
  ro: ["Cotație", "Plată", "Documente", "Partener alocat", "Partener acceptat", "Șofer alocat", "În tranzit", "Livrat"],
  en: ["Quote", "Payment", "Documents", "Partner assigned", "Partner accepted", "Driver assigned", "In transit", "Delivered"],
};

function ProgressDiagram({ order, docStatus, locale }) {
  const labels = STEP_LABELS[locale] || STEP_LABELS.en;
  const st = order.status;
  const stages = [
    true,
    order.payment_status === "paid",
    docStatus ? docStatus.complete : false,
    !!order.partner_id,
    order.partner_acceptance === "accepted",
    !!order.driver_id,
    ["in_transit", "delivered"].includes(st),
    st === "delivered",
  ];
  // Current step = last done index (or first not-done).
  let current = 0;
  stages.forEach((d, i) => { if (d) current = i; });
  const donePct = Math.round((stages.filter(Boolean).length / stages.length) * 100);
  return (
    <div className="px-6 py-5 border-b border-slate-100" data-testid="client-progress">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Stato del trasporto</span>
        <span className="text-sm font-bold text-blue-800" data-testid="progress-percent">{donePct}%</span>
      </div>
      <div className="h-1.5 w-full bg-slate-100 rounded-full mb-4 overflow-hidden">
        <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${donePct}%` }} />
      </div>
      <div className="flex items-center gap-0 overflow-x-auto pb-1">
        {labels.map((label, i) => {
          const done = stages[i];
          const isCurrent = i === current;
          return (
            <div key={label} className="flex items-center shrink-0" data-testid={`progress-step-${i}`} data-done={done ? "1" : "0"}>
              <div className="flex flex-col items-center w-20">
                <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-semibold border-2 ${
                  done ? "bg-emerald-600 border-emerald-600 text-white" : isCurrent ? "bg-white border-blue-700 text-blue-700" : "bg-white border-slate-300 text-slate-400"}`}>
                  {done ? "✓" : i + 1}
                </div>
                <span className={`mt-1.5 text-[10px] text-center leading-tight ${done ? "text-emerald-700" : isCurrent ? "text-blue-700 font-medium" : "text-slate-400"}`}>{label}</span>
              </div>
              {i < labels.length - 1 && <div className={`h-0.5 w-6 -mt-5 ${stages[i + 1] ? "bg-emerald-500" : "bg-slate-200"}`} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, value, action, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400 text-xs font-semibold uppercase tracking-wider">
        <Icon size={15} /> {title}
      </div>
      <div className="mt-2 text-sm font-medium text-slate-800 break-words">{value}</div>
      {action}
    </div>
  );
}

export function ClientDashboard() {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const [orders, setOrders] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [support, setSupport] = useState({});
  const [docStatus, setDocStatus] = useState(null);
  const [paying, setPaying] = useState(false);
  const [cb, setCb] = useState({ open: false, quote: null, phone: "", preferred_date: "", preferred_time: "", note: "" });
  const [cbSending, setCbSending] = useState(false);

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
    api.get("/quotes").then(({ data }) => setQuotes(data)).catch(() => setQuotes([]));
    api.get("/invoices").then(({ data }) => setInvoices(data)).catch(() => setInvoices([]));
    api.get("/public/settings").then(({ data }) => setSupport(data.company || {})).catch(() => {});
  }, []);

  const active = (orders || []).find((o) => ACTIVE.includes(o.status)) || null;
  const past = (orders || []).filter((o) => !active || o.id !== active.id);

  useEffect(() => {
    if (!active?.id) { setDocStatus(null); return; }
    api.get(`/documents/required/${active.id}`).then(({ data }) => setDocStatus(data)).catch(() => setDocStatus(null));
  }, [active?.id]);

  const pay = async () => {
    setPaying(true);
    try { const { data } = await api.post(`/orders/${active.id}/pay`, { origin_url: window.location.origin }); if (data?.url) window.location.href = data.url; else toast.error(t("common.error") || "Error"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPaying(false); }
  };

  const payQuote = async (quote) => {
    try { const { data } = await api.post(`/public/quote/${quote.id}/checkout`, { origin_url: window.location.origin }); if (data?.url) window.location.href = data.url; else toast.error(t("common.error") || "Error"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendCallback = async () => {
    setCbSending(true);
    try {
      await api.post("/public/callback-request", {
        name: user?.name || "", email: user?.email || "",
        phone: cb.phone || cb.quote?.client_phone || user?.phone || "",
        preferred_date: cb.preferred_date, preferred_time: cb.preferred_time,
        note: cb.note, quote_id: cb.quote?.id || "",
      });
      toast.success(t("cd.callbackSent"));
      setCb({ open: false, quote: null, phone: "", preferred_date: "", preferred_time: "", note: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCbSending(false); }
  };

  const trackingUrl = active?.tracking_code ? `${window.location.origin}/track/${active.tracking_code}` : "";
  const copyTracking = () => { navigator.clipboard?.writeText(trackingUrl).then(() => toast.success(t("cd.copied"))).catch(() => {}); };
  const lastEvent = active?.timeline?.length ? active.timeline[active.timeline.length - 1] : null;
  const supportEmail = support.email || "info@rimpatrio24.it";
  const supportPhone = support.phone || "";

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      <div>
        <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">{t("cd.welcome")}, {user?.name}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("nav.dashboard")}</p>
      </div>

      {orders === null ? (
        <div className="text-slate-400">{t("common.loading")}</div>
      ) : active ? (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="client-active-order">
            {active.payment_status !== "paid" && active.status !== "pending" && active.total > 0 && (
              <div className="bg-amber-50 border-b border-amber-200 px-6 py-4 flex items-center justify-between gap-3 flex-wrap" data-testid="client-pay-banner">
                <div className="flex items-center gap-2 text-amber-800">
                  <AlertCircle size={18} />
                  <span className="text-sm font-semibold">{t("cd.payPrompt")} · {money(active.total, active.currency)}</span>
                </div>
                <Button onClick={pay} disabled={paying} size="lg" className="bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="client-pay-banner-btn">
                  <CreditCard size={18} className="mr-1.5" /> {paying ? "..." : t("order.payNow")}
                </Button>
              </div>
            )}
            {active.payment_status === "paid" && active.mandate_status && active.mandate_status !== "signed" && (
              <div className="bg-rose-50 border-b border-rose-200 px-6 py-4 flex items-center justify-between gap-3 flex-wrap" data-testid="client-mandate-banner">
                <div className="flex items-center gap-2 text-rose-700">
                  <FileSignature size={18} />
                  <div>
                    <div className="text-sm font-semibold">{t("cd.mandateTitle")}{active.mandate_completion != null ? ` · ${active.mandate_completion}%` : ""}</div>
                    <div className="text-xs text-rose-600/80">{t("cd.mandateSub")}</div>
                  </div>
                </div>
                <Link to={`/orders/${active.id}?tab=mandate`}>
                  <Button size="lg" className="bg-rose-600 hover:bg-rose-700 font-semibold" data-testid="client-mandate-banner-btn">
                    <FileSignature size={18} className="mr-1.5" /> {t("cd.mandateBtn")}
                  </Button>
                </Link>
              </div>
            )}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">{t("cd.activeOrder")}</div>
                <h2 className="font-display text-2xl font-bold text-slate-900">{active.number}</h2>
                <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><MapPin size={14} /> {active.origin} → {active.destination}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={active.status} />
                {active.payment_status === "paid" ? (
                  <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="client-paid-badge"><CheckCircle2 size={13} /> {t("order.paid")}</span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200" data-testid="client-unpaid-badge"><AlertCircle size={13} /> {t("order.unpaid")}</span>
                )}
              </div>
            </div>

            <ProgressDiagram order={active} docStatus={docStatus} locale={locale} />

            {/* Transport status + documents summary */}
            <div className="px-6 py-4 grid sm:grid-cols-3 gap-4 border-b border-slate-100 bg-slate-50/60">
              <div data-testid="client-transport-status">
                <div className="text-xs text-slate-400">{t("cd.transport")}</div>
                <div className="text-sm font-medium text-slate-800 capitalize mt-0.5">{(active.status || "").replace(/_/g, " ")}</div>
                {active.eta && <div className="text-xs text-slate-500 mt-0.5">{t("cd.eta")}: {formatDateTime(active.eta)}</div>}
              </div>
              <div data-testid="client-last-update">
                <div className="text-xs text-slate-400">{t("cd.lastUpdate")}</div>
                <div className="text-sm font-medium text-slate-800 mt-0.5">{lastEvent ? formatDateTime(lastEvent.timestamp) : "—"}</div>
                {lastEvent?.location && <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><MapPin size={11} /> {lastEvent.location}</div>}
              </div>
              <div data-testid="client-docs-status">
                <div className="text-xs text-slate-400">{t("cd.documents")}</div>
                {docStatus ? (
                  docStatus.complete ? (
                    <div className="text-sm font-medium text-emerald-700 mt-0.5 flex items-center gap-1"><CheckCircle2 size={13} /> {t("cd.docsComplete")}</div>
                  ) : (
                    <div className="text-sm font-medium text-amber-700 mt-0.5 flex items-center gap-1"><AlertCircle size={13} /> {docStatus.missing.length} {t("cd.docsMissing")}</div>
                  )
                ) : <div className="text-sm text-slate-400 mt-0.5">—</div>}
              </div>
            </div>

            {/* Public tracking link */}
            {trackingUrl && (
              <div className="px-6 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-400">{t("cd.trackingLink")}:</span>
                <code className="text-xs bg-slate-100 rounded px-2 py-1 text-slate-700 truncate max-w-[260px]" data-testid="client-tracking-link">{trackingUrl}</code>
                <Button size="sm" variant="ghost" onClick={copyTracking} data-testid="client-copy-link"><Copy size={13} className="mr-1" /> {t("cd.copyLink")}</Button>
              </div>
            )}

            <div className="p-6 flex flex-wrap gap-3">
              {active.tracking_code && (
                <a href={`/track/${active.tracking_code}`} target="_blank" rel="noreferrer">
                  <Button variant="outline" data-testid="client-track-btn"><Navigation size={16} className="mr-1.5" /> {t("cd.track")}</Button>
                </a>
              )}
              <Link to={`/orders/${active.id}`}><Button variant="outline" data-testid="client-upload-btn"><Upload size={16} className="mr-1.5" /> {t("cd.uploadDocs")}</Button></Link>
              {active.payment_status !== "paid" && active.status !== "pending" && active.total > 0 && (
                <Button onClick={pay} disabled={paying} className="bg-emerald-600 hover:bg-emerald-700" data-testid="client-pay-btn"><CreditCard size={16} className="mr-1.5" /> {paying ? "..." : `${t("order.payNow")} · ${money(active.total, active.currency)}`}</Button>
              )}
              {active.invoice_id && (
                <a href={pdfUrl(`invoices/${active.invoice_id}/pdf`)} target="_blank" rel="noreferrer">
                  <Button variant="outline" data-testid="client-invoice-btn"><FileDown size={16} className="mr-1.5" /> {t("cd.downloadInvoice")}</Button>
                </a>
              )}
              <a href={`mailto:${supportEmail}`}><Button variant="outline" data-testid="client-support-btn"><LifeBuoy size={16} className="mr-1.5" /> {t("cd.contactSupport")}</Button></a>
              <Link to={`/orders/${active.id}?tab=chat`}>
                <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="client-chat-support-btn">
                  <Headset size={16} className="mr-1.5" /> Parla con il supporto · 24/7
                </Button>
              </Link>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="client-timeline">
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Clock size={18} /> {t("cd.timeline")}</h3>
            <div className="space-y-4">
              {(active.timeline || []).slice().reverse().map((ev) => (
                <div key={ev.timestamp} className="flex gap-3">
                  <div className="mt-1 h-2 w-2 rounded-full bg-blue-700 shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-slate-700 capitalize">{(ev.status || "").replace(/_/g, " ")}</div>
                    {ev.note && <div className="text-xs text-slate-500">{ev.note}</div>}
                    <div className="text-xs text-slate-400">{formatDateTime(ev.timestamp)}</div>
                  </div>
                </div>
              ))}
              {(active.timeline || []).length === 0 && <p className="text-sm text-slate-400">{t("common.none")}</p>}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 text-center" data-testid="client-no-active">
          <Navigation className="mx-auto h-12 w-12 text-slate-300" />
          <h2 className="font-display text-xl font-semibold text-slate-900 mt-4">{t("cd.noActive")}</h2>
          <p className="text-sm text-slate-500 mt-1">{t("cd.noActiveSub")}</p>
          <div className="mt-6 flex gap-3 justify-center">
            <a href="/"><Button className="bg-blue-900 hover:bg-blue-800" data-testid="client-new-quote-btn"><Plus size={16} className="mr-1.5" /> {t("cd.newQuote")}</Button></a>
            <Link to="/orders"><Button variant="outline" data-testid="client-past-orders-btn">{t("cd.pastOrders")}</Button></Link>
          </div>
        </div>
      )}

      {/* Quotes */}
      {quotes.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="client-quotes">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><FileText size={18} /> {t("cd.quotes")} <span className="text-xs font-normal text-slate-400">({quotes.length})</span></h3>
          <div className="space-y-3">
            {quotes.map((q) => {
              const isOpen = OPEN_QUOTE.includes((q.status || "").toLowerCase());
              return (
              <div key={q.id} className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 hover:bg-slate-50" data-testid="client-quote-row">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800">{q.number}</div>
                  <div className="text-xs text-slate-500">{q.origin} → {q.destination}</div>
                  <div className="text-xs text-slate-400">{formatDateTime(q.created_at)}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-right mr-2">
                    <div className="text-xs text-slate-400">{t("cd.estTotal")}</div>
                    <div className="text-sm font-semibold text-slate-800">{money(q.total, q.currency)}</div>
                  </div>
                  <StatusBadge status={q.status} />
                  {isOpen && q.total > 0 && (
                    <Button size="sm" onClick={() => payQuote(q)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="client-quote-pay-btn"><CreditCard size={14} className="mr-1" /> {t("order.payNow")}</Button>
                  )}
                  {isOpen && (
                    <Button size="sm" variant="outline" onClick={() => setCb({ ...cb, open: true, quote: q, phone: q.client_phone || user?.phone || "" })} data-testid="client-quote-callback-btn"><PhoneCall size={14} className="mr-1" /> {t("wiz.requestCallback")}</Button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Useful info cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <InfoCard icon={FileText} title={t("cd.docsRequired")} testid="client-card-docs"
          value={docStatus ? (docStatus.complete ? t("cd.docsComplete") : `${docStatus.missing.length} ${t("cd.docsMissing")}`) : (active ? "—" : t("common.none"))} />
        <InfoCard icon={LifeBuoy} title={t("cd.support")} testid="client-card-support"
          value={supportEmail}
          action={<a href={`mailto:${supportEmail}`} className="text-xs text-blue-700 hover:underline mt-1 inline-block">{t("cd.contactSupport")}</a>} />
        <InfoCard icon={Phone} title={t("cd.emergency")} testid="client-card-emergency"
          value={supportPhone ? <a href={`tel:${supportPhone}`} className="text-blue-700 hover:underline">{supportPhone}</a> : "—"} />
        <InfoCard icon={ReceiptEuro} title={t("cd.invoices")} testid="client-card-invoices"
          value={`${invoices.length}`}
          action={invoices.length > 0 ? <Link to="/invoices" className="text-xs text-blue-700 hover:underline mt-1 inline-block">{t("common.view")}</Link> : null} />
      </div>

      {/* Order history */}
      {past.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="client-past-orders">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">{t("cd.pastOrders")} <span className="text-xs font-normal text-slate-400">({past.length})</span></h3>
          <div className="space-y-2">
            {past.slice(0, 20).map((o) => (
              <Link key={o.id} to={`/orders/${o.id}`} className="flex items-center justify-between p-2 -mx-2 rounded-lg hover:bg-slate-50">
                <div><div className="text-sm font-medium text-slate-800">{o.number}</div><div className="text-xs text-slate-400">{o.origin} → {o.destination}</div></div>
                <div className="flex items-center gap-2">
                  {o.payment_status === "paid" && <span className="text-xs text-emerald-600">{t("order.paid")}</span>}
                  <StatusBadge status={o.status} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Callback dialog */}
      <Dialog open={cb.open} onOpenChange={(o) => setCb({ ...cb, open: o })}>
        <DialogContent data-testid="client-callback-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("wiz.requestCallback")}</DialogTitle>
            <DialogDescription>{t("wiz.contactSoon")}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5 col-span-2"><Label>{t("cd.phone")}</Label><Input value={cb.phone} onChange={(e) => setCb({ ...cb, phone: e.target.value })} data-testid="callback-phone" />{cb.phone ? <p className="text-xs text-slate-400">{t("cd.phonePrefill")}</p> : null}</div>
            <div className="space-y-1.5"><Label>{t("cd.preferredDate")}</Label><Input type="date" value={cb.preferred_date} onChange={(e) => setCb({ ...cb, preferred_date: e.target.value })} data-testid="callback-date" /></div>
            <div className="space-y-1.5"><Label>{t("cd.preferredTime")}</Label><Input type="time" value={cb.preferred_time} onChange={(e) => setCb({ ...cb, preferred_time: e.target.value })} data-testid="callback-time" /></div>
            <div className="space-y-1.5 col-span-2"><Label>{t("cd.note")}</Label><Input value={cb.note} onChange={(e) => setCb({ ...cb, note: e.target.value })} data-testid="callback-note" /></div>
          </div>
          <DialogFooter>
            <Button onClick={sendCallback} disabled={cbSending || !cb.phone} className="bg-blue-900 hover:bg-blue-800" data-testid="callback-submit">{cbSending ? "..." : t("cd.callbackSubmit")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
